import { forwardRef, useImperativeHandle, useMemo, useRef, useState, useCallback } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Spin } from "antd"
import { DragEndEvent, useDroppable } from "@dnd-kit/core"
import { ScheduleState, buildUnifiedEvents, buildEventDisplayInfo, mapDateRangeViewEventToScheduledEvent, UnifiedEventMetadata } from "../scheduleModel"
import { filterConsultations, filterEvents, filterTasks } from '../scheduleFilter'
import { useDateRangeViewEventsQuery, useUpdateScheduledEventMutation, useUpsertTaskEventMutation, useConsultationsByDateRangeQuery, useUpdateConsultationTimeMutation, useTasksByDateRangeQuery } from "../scheduleQueries"
import { DateRangeViewEvent, ConsultationCalendarEvent, SchedulingJob, TaskCalendarEvent } from "../scheduleApi"
import WeekViewDay from "./WeekViewDay"
import WeekViewSpanningEvent from "./WeekViewSpanningEvent"
import WeekViewSpanningTask from "./WeekViewSpanningTask"
import { buildMoveEventRequest, buildResizeEventRequest, HOUR_HEIGHT, START_HOUR, END_HOUR, TIME_COLUMN_WIDTH, calculateCollapseEndHour, calculateDayColumnWidths } from "./weekViewModel"
import { isSpanningEvent, isSpanningTask, calculateWeekSpanningLayouts, SPANNING_EVENT_GAP, calculateReservedHeight, SpanningEventSegment, buildResizeSpanningEventRequest, hasSpanningEventOnDay, hasSpanningTaskOnDay, WEEK_VIEW_SPANNING_ROW_HEIGHT } from "../MonthView/spanningEventHelpers"
import { findCrewMemberByEmail } from '../crewModel'
import JobDrawer from "../JobDrawer/JobDrawer"
import EditConsultationModal from "../EditConsultation/EditConsultationModal"
import TaskEventModal from "../EditTask/TaskEventModal"
import JobPickerModal from "../CreateEvent/JobPickerModal"
import CreateEventFromJobModal from "../CreateEvent/CreateEventFromJobModal"
import ScheduleEventTypePickerModal from "../CreateEvent/ScheduleEventTypePickerModal"
import { useSchedule } from "../ScheduleContext"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsFromDottedLine } from '@fortawesome/pro-solid-svg-icons'
import OptimizeButton from "../OptimizeSchedule/OptimizeButton"
import './ScheduleWeekView.css'

export type Props = {
  state: ScheduleState
  onDateChange: (date: Dayjs) => void
  onOpenOptimize: (date: Dayjs) => void
}

export type ScheduleWeekViewHandle = {
  handleDragEnd: (event: DragEndEvent) => void
}

type SpanningDropZoneProps = {
  day: Dayjs
  width: number
  isResizing?: boolean
}

const SpanningDropZone = ({ day, width, isResizing }: SpanningDropZoneProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `spanning-drop-${day.format('YYYY-MM-DD')}`,
    data: { date: day },
  })

  const showHighlight = isOver && !isResizing

  return (
    <div
      ref={setNodeRef}
      style={{
        width,
        flexShrink: 0,
        borderRight: '1px solid #e8e8e8',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: showHighlight ? '#e6f7ff' : 'transparent',
      }}
    />
  )
}

type JobPickerState = {
  open: boolean
  day: Dayjs | null
  startMinutes: number
  durationMinutes: number | null
}

type CreateEventState = {
  open: boolean
  job: SchedulingJob | null
  day: Dayjs | null
  startMinutes: number
  durationMinutes: number | null
}

const ScheduleWeekView = forwardRef<ScheduleWeekViewHandle, Props>(({ state, onDateChange, onOpenOptimize }, ref) => {
  const { start: weekStart, end: weekEnd } = state.visibleDateRange
  const { crew } = useSchedule()
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationCalendarEvent | null>(null)
  const [consultationModalOpen, setConsultationModalOpen] = useState(false)
  const [earlyHoursExpanded, setEarlyHoursExpanded] = useState(false)
  const [eventTypePickerState, setEventTypePickerState] = useState<{ open: boolean, day: Dayjs | null, startMinutes: number, durationMinutes: number | null }>({ open: false, day: null, startMinutes: 0, durationMinutes: null })
  const [jobPickerState, setJobPickerState] = useState<JobPickerState>({ open: false, day: null, startMinutes: 0, durationMinutes: null })
  const [createEventState, setCreateEventState] = useState<CreateEventState>({ open: false, job: null, day: null, startMinutes: 0, durationMinutes: null })
  const [selectedTask, setSelectedTask] = useState<TaskCalendarEvent | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [taskModalDay, setTaskModalDay] = useState<Dayjs | null>(null)
  const updateEventMutation = useUpdateScheduledEventMutation()
  const updateConsultationMutation = useUpdateConsultationTimeMutation()
  const upsertTaskMutation = useUpsertTaskEventMutation()

  const weekDays: Dayjs[] = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart.format('YYYY-MM-DD')]
  )

  const { data, isLoading } = useDateRangeViewEventsQuery(
    weekStart.format('YYYY-MM-DD'),
    weekEnd.format('YYYY-MM-DD')
  )

  const { data: consultationsData, isLoading: consultationsLoading } = useConsultationsByDateRangeQuery(
    weekStart.format('YYYY-MM-DD'),
    weekEnd.format('YYYY-MM-DD')
  )

  const { data: tasksData, isLoading: tasksLoading } = useTasksByDateRangeQuery(
    weekStart.format('YYYY-MM-DD'),
    weekEnd.format('YYYY-MM-DD')
  )

  const unifiedResult = useMemo(() => {
    if (!data) {
      return null
    }
    return buildUnifiedEvents(data)
  }, [data])

  const { events, calendarEventVisits, visits } = unifiedResult ?? { events: [], calendarEventVisits: [], visits: [] }
  const { visitWorkareaStatuses, timesheets } = data ?? { visitWorkareaStatuses: [], timesheets: [] }
  const eventMetadata: Map<number, UnifiedEventMetadata> = unifiedResult?.eventMetadata ?? new Map()

  const consultations = useMemo(() => {
    return filterConsultations(consultationsData?.consultations ?? [], state.filters, crew ?? [])
  }, [consultationsData, state.filters, crew])

  const filteredEvents = useMemo(() => {
    return filterEvents(
      events,
      state.filters,
      visits,
      visitWorkareaStatuses,
      timesheets,
      crew ?? [],
      calendarEventVisits,
      state.visibleDateRange,
      eventMetadata
    )
  }, [events, state.filters, visits, visitWorkareaStatuses, timesheets, crew, calendarEventVisits, state.visibleDateRange, eventMetadata])

  const { spanningEvents, singleDayEvents } = useMemo(() => {
    const spanning: DateRangeViewEvent[] = []
    const singleDay: DateRangeViewEvent[] = []

    for (const event of filteredEvents) {
      if (isSpanningEvent(event)) {
        spanning.push(event)
      } else {
        singleDay.push(event)
      }
    }

    return { spanningEvents: spanning, singleDayEvents: singleDay }
  }, [filteredEvents])

  const { spanningTasks, singleDayTasks } = useMemo(() => {
    const allTasks = filterTasks(tasksData?.tasks ?? [], state.filters, crew ?? [])
    const spanning: TaskCalendarEvent[] = []
    const singleDay: TaskCalendarEvent[] = []

    for (const task of allTasks) {
      if (isSpanningTask(task)) {
        spanning.push(task)
      } else {
        singleDay.push(task)
      }
    }

    return { spanningTasks: spanning, singleDayTasks: singleDay }
  }, [tasksData, state.filters, crew])

  const weekSpanningLayouts = useMemo(() => {
    return calculateWeekSpanningLayouts(spanningEvents, [weekStart], spanningTasks)
  }, [spanningEvents, weekStart.format('YYYY-MM-DD'), spanningTasks])

  const spanningLayout = weekSpanningLayouts[0] ?? { segments: [], taskSegments: [], totalRows: 0, rowsPerDay: new Array(7).fill(0) }
  const spanningHeight = calculateReservedHeight(spanningLayout.totalRows, WEEK_VIEW_SPANNING_ROW_HEIGHT)

  const collapseEndHour = useMemo(() => {
    return calculateCollapseEndHour(singleDayEvents, consultations, singleDayTasks)
  }, [singleDayEvents, consultations, singleDayTasks])

  const effectiveStartHour = earlyHoursExpanded ? START_HOUR : collapseEndHour
  const visibleHours = END_HOUR - effectiveStartHour
  const topOffset = effectiveStartHour * HOUR_HEIGHT

  const singleDayEventsByDay: Map<string, DateRangeViewEvent[]> = useMemo(() => {
    const map = new Map<string, DateRangeViewEvent[]>()

    weekDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayEvents = singleDayEvents.filter((event) => dayjs(event.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayEvents)
    })

    return map
  }, [weekDays, singleDayEvents])

  const tasksByDay: Map<string, TaskCalendarEvent[]> = useMemo(() => {
    const map = new Map<string, TaskCalendarEvent[]>()

    weekDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayTasks = singleDayTasks.filter((event) => dayjs(event.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayTasks)
    })

    return map
  }, [weekDays, singleDayTasks])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const activeId = String(active.id)

    if (activeId.startsWith('job-')) {
      return
    }

    if (active.data.current?.isResize) {
      return
    }

    if (over && active.data.current?.isConsultation && active.data.current?.consultation) {
      const consultation = active.data.current.consultation as ConsultationCalendarEvent
      const targetDate = over.data.current?.date as Dayjs | undefined
      const targetMinutes = over.data.current?.minutes as number | undefined

      if (targetDate && targetMinutes !== undefined) {
        const consultationStart = dayjs(consultation.start)
        const consultationEnd = dayjs(consultation.end)
        const durationMinutes = consultationEnd.diff(consultationStart, 'minute')

        const newStart = targetDate.hour(Math.floor(targetMinutes / 60)).minute(targetMinutes % 60).second(0)
        const newEnd = newStart.add(durationMinutes, 'minute')

        updateConsultationMutation.mutate({
          id: consultation.id,
          startUtc: newStart.toISOString(),
          endUtc: newEnd.toISOString(),
          start: newStart.format('YYYY-MM-DDTHH:mm:ss'),
          end: newEnd.format('YYYY-MM-DDTHH:mm:ss'),
          startDate: weekStart.format('YYYY-MM-DD'),
          endDate: weekEnd.format('YYYY-MM-DD'),
        })
      }
      return
    }

    if (over && active.data.current?.isTask && active.data.current?.event) {
      const droppedEvent = active.data.current.event as TaskCalendarEvent
      const targetDate = over.data.current?.date as Dayjs | undefined
      const targetMinutes = over.data.current?.minutes as number | undefined

      if (targetDate) {
        const originalStart = dayjs(droppedEvent.start)
        const originalEnd = dayjs(droppedEvent.end)
        const durationMinutes = originalEnd.diff(originalStart, 'minutes')

        const newStart = targetMinutes !== undefined
          ? targetDate.hour(Math.floor(targetMinutes / 60)).minute(targetMinutes % 60).second(0)
          : targetDate.hour(originalStart.hour()).minute(originalStart.minute()).second(0)
        const newEnd = newStart.add(durationMinutes, 'minute')

        upsertTaskMutation.mutate({
          eventId: droppedEvent.id,
          title: droppedEvent.title,
          notes: droppedEvent.notes,
          start: newStart.toISOString(),
          end: newEnd.toISOString(),
          dayStartTime: null,
          estimatedDurationInMinutes: durationMinutes,
          crewLeadId: findCrewMemberByEmail(crew, droppedEvent.crewForemanEmail)?.id ?? null,
          crewMemberIds: droppedEvent.crewMemberIds || '',
          allDay: droppedEvent.allDay,
          sendCrewLeadNotifications: droppedEvent.sendCrewLeadNotifications,
          completedAt: droppedEvent.completedAt,
          frozen: droppedEvent.frozen,
        })
      }
      return
    }

    if (over && active.data.current?.event) {
      const droppedEvent = active.data.current.event as DateRangeViewEvent
      const targetDate = over.data.current?.date as Dayjs | undefined

      if (activeId.startsWith('spanning-event-')) {
        if (targetDate) {
          const segment = active.data.current.segment as SpanningEventSegment
          const eventStart = dayjs(droppedEvent.start)
          const segmentStartDate = weekStart.add(segment.startDayIndex, 'day')
          const offsetDays = eventStart.startOf('day').diff(segmentStartDate.startOf('day'), 'day')
          const newStartDate = targetDate.add(offsetDays, 'day')

          const request = buildMoveEventRequest({
            event: droppedEvent,
            targetDate: newStartDate,
            targetMinutes: null,
            crew: crew ?? [],
          })

          if (request) {
            updateEventMutation.mutate(request)
          }
        }
      } else {
        const targetMinutes = over.data.current?.minutes as number | undefined

        if (targetDate && targetMinutes !== undefined) {
          const request = buildMoveEventRequest({
            event: droppedEvent,
            targetDate,
            targetMinutes,
            crew: crew ?? [],
          })

          if (request) {
            updateEventMutation.mutate(request)
          }
        }
      }
    }
  }

  useImperativeHandle(ref, () => ({
    handleDragEnd
  }))

  const handleEventClick = (event: DateRangeViewEvent) => {
    setSelectedEventId(event.id)
    setDrawerOpen(true)
  }

  const handleEventResize = (event: DateRangeViewEvent, newDurationMinutes: number) => {
    const request = buildResizeEventRequest({
      event,
      newDurationMinutes,
      crew: crew ?? [],
    })

    if (request) {
      updateEventMutation.mutate(request)
    }
  }

  const handleSpanningEventResize = (event: DateRangeViewEvent, newEndDate: Dayjs) => {
    const request = buildResizeSpanningEventRequest({
      event,
      newEndDate,
      crew: crew ?? [],
    })

    if (request) {
      updateEventMutation.mutate(request)
    }
  }

  const handleSpanningTaskResize = (task: TaskCalendarEvent, newEndDate: Dayjs) => {
    const originalStart = dayjs(task.start)
    const originalEnd = dayjs(task.end)
    const newEnd = newEndDate
      .hour(originalEnd.hour())
      .minute(originalEnd.minute())
      .second(originalEnd.second())
    const durationMinutes = newEnd.diff(originalStart, 'minutes')

    upsertTaskMutation.mutate({
      eventId: task.id,
      title: task.title,
      notes: task.notes,
      start: originalStart.toISOString(),
      end: newEnd.toISOString(),
      dayStartTime: null,
      estimatedDurationInMinutes: durationMinutes,
      crewLeadId: findCrewMemberByEmail(crew, task.crewForemanEmail)?.id ?? null,
      crewMemberIds: task.crewMemberIds || '',
      allDay: task.allDay,
      sendCrewLeadNotifications: task.sendCrewLeadNotifications,
      completedAt: task.completedAt,
      frozen: task.frozen,
    })
  }

  const eventDisplayInfo = useMemo(() => {
    return buildEventDisplayInfo(filteredEvents, calendarEventVisits, visits, visitWorkareaStatuses, timesheets, crew ?? [], state.visibleDateRange, eventMetadata)
  }, [filteredEvents, calendarEventVisits, visits, visitWorkareaStatuses, timesheets, crew, state.visibleDateRange, eventMetadata])

  const consultationsByDay: Map<string, ConsultationCalendarEvent[]> = useMemo(() => {
    const map = new Map<string, ConsultationCalendarEvent[]>()

    weekDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayConsultations = consultations.filter((consultation) => dayjs(consultation.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayConsultations)
    })

    return map
  }, [weekDays, consultations])

  const observerRef = useRef<ResizeObserver | null>(null)
  const scrollListenerRef = useRef<(() => void) | null>(null)
  const headerScrollRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [shadowLeft, setShadowLeft] = useState(false)
  const [shadowRight, setShadowRight] = useState(false)

  const measureRef = useCallback((node: HTMLDivElement | null) => {
    if (scrollListenerRef.current) {
      scrollListenerRef.current()
      scrollListenerRef.current = null
    }
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (node) {
      const updateShadowsAndHeader = () => {
        setShadowLeft(node.scrollLeft > 0)
        setShadowRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 1)
        if (headerScrollRef.current && headerScrollRef.current.scrollLeft !== node.scrollLeft) {
          headerScrollRef.current.scrollLeft = node.scrollLeft
        }
      }

      setContainerWidth(node.clientWidth)
      updateShadowsAndHeader()

      node.addEventListener('scroll', updateShadowsAndHeader, { passive: true })
      scrollListenerRef.current = () => node.removeEventListener('scroll', updateShadowsAndHeader)

      const observer = new ResizeObserver(() => {
        setContainerWidth(node.clientWidth)
        updateShadowsAndHeader()
      })
      observer.observe(node)
      observerRef.current = observer
    }
  }, [])

  const dayColumnWidths = useMemo(() => {
    if (containerWidth === 0) {
      return Array(7).fill(0)
    }
    const hasSpanningByDay = weekDays.map((_, index) =>
      hasSpanningEventOnDay(spanningLayout.segments, index) || hasSpanningTaskOnDay(spanningLayout.taskSegments, index)
    )
    return calculateDayColumnWidths(weekDays, singleDayEventsByDay, consultationsByDay, containerWidth, tasksByDay, hasSpanningByDay)
  }, [weekDays, singleDayEventsByDay, consultationsByDay, containerWidth, tasksByDay, spanningLayout.segments, spanningLayout.taskSegments])

  const totalContentWidth = TIME_COLUMN_WIDTH + dayColumnWidths.reduce((s, w) => s + w, 0)

  const handleConsultationResize = (consultation: ConsultationCalendarEvent, newDurationMinutes: number) => {
    const startTime = dayjs(consultation.start)
    const newEndTime = startTime.add(newDurationMinutes, 'minute')

    updateConsultationMutation.mutate({
      id: consultation.id,
      startUtc: startTime.toISOString(),
      endUtc: newEndTime.toISOString(),
      start: consultation.start,
      end: newEndTime.format('YYYY-MM-DDTHH:mm:ss'),
      startDate: weekStart.format('YYYY-MM-DD'),
      endDate: weekEnd.format('YYYY-MM-DD'),
    })
  }

  const handleConsultationClick = (consultation: ConsultationCalendarEvent) => {
    setSelectedConsultation(consultation)
    setConsultationModalOpen(true)
  }

  const handleTaskClick = (event: TaskCalendarEvent) => {
    setSelectedTask(event)
    setTaskModalOpen(true)
  }

  const handleTaskResize = (event: TaskCalendarEvent, newDurationMinutes: number) => {
    const originalStart = dayjs(event.start)
    const newEnd = originalStart.add(newDurationMinutes, 'minute')

    upsertTaskMutation.mutate({
      eventId: event.id,
      title: event.title,
      notes: event.notes,
      start: originalStart.toISOString(),
      end: newEnd.toISOString(),
      dayStartTime: null,
      estimatedDurationInMinutes: newDurationMinutes,
      crewLeadId: findCrewMemberByEmail(crew, event.crewForemanEmail)?.id ?? null,
      crewMemberIds: event.crewMemberIds || '',
      allDay: event.allDay,
      sendCrewLeadNotifications: event.sendCrewLeadNotifications,
      completedAt: event.completedAt,
      frozen: event.frozen,
    })
  }

  const handleCalendarClick = (day: Dayjs, minutes: number) => {
    setEventTypePickerState({ open: true, day, startMinutes: minutes, durationMinutes: null })
  }

  const handleDragSelect = (day: Dayjs, startMinutes: number, durationMinutes: number) => {
    setEventTypePickerState({ open: true, day, startMinutes, durationMinutes })
  }

  const handleEventTypePickerScheduleJob = () => {
    setJobPickerState({
      open: true,
      day: eventTypePickerState.day,
      startMinutes: eventTypePickerState.startMinutes,
      durationMinutes: eventTypePickerState.durationMinutes,
    })
    setEventTypePickerState(prev => ({ ...prev, open: false }))
  }

  const handleEventTypePickerAddTask = () => {
    setTaskModalDay(eventTypePickerState.day)
    setTaskModalOpen(true)
    setEventTypePickerState(prev => ({ ...prev, open: false }))
  }

  const handleJobSelected = (job: SchedulingJob) => {
    setJobPickerState(prev => ({ ...prev, open: false }))
    setCreateEventState({
      open: true,
      job,
      day: jobPickerState.day,
      startMinutes: jobPickerState.startMinutes,
      durationMinutes: jobPickerState.durationMinutes,
    })
  }

  if (isLoading || consultationsLoading || tasksLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '66vh',
          gap: 8,
        }}
      >
        <Spin size="large" />
        <div>Loading...</div>
      </div>
    )
  }

  const selectedEvent = selectedEventId !== null
    ? events.find(e => e.id === selectedEventId) ?? null
    : null
  const selectedEventData = selectedEvent ? {
    event: mapDateRangeViewEventToScheduledEvent(selectedEvent),
    calendarEventVisits,
    visits,
    visitWorkareaStatuses,
    timesheets,
  } : null

  const hours = Array.from({ length: visibleHours }, (_, i) => {
    const hour = effectiveStartHour + i
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:00 ${period}`
  })

  const formatCollapseLabel = () => {
    const formatHour = (h: number) => {
      const period = h >= 12 ? 'PM' : 'AM'
      const display = h > 12 ? h - 12 : h === 0 ? 12 : h
      return `${display} ${period}`
    }
    return `${formatHour(0)} – ${formatHour(collapseEndHour)}: No Events`
  }

  const hasSpanningItems = spanningLayout.totalRows > 0

  const dayWidthsSum = totalContentWidth - TIME_COLUMN_WIDTH

  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 80 }}>
      <div
        style={{
          position: 'sticky',
          top: 'var(--title-bar-height, 0px)',
          zIndex: 30,
          backgroundColor: '#fff',
        }}
      >
        <div
          ref={headerScrollRef}
          style={{
            overflowX: 'hidden',
            border: '1px solid #e8e8e8',
            borderRight: 0,
            borderBottom: 0,
          }}
        >
          <div style={{ minWidth: totalContentWidth, display: 'flex', flexShrink: 0 }}>
            <div
              style={{
                width: TIME_COLUMN_WIDTH,
                flexShrink: 0,
                borderRight: '1px solid #e8e8e8',
                borderBottom: '1px solid #e8e8e8',
                backgroundColor: '#fafbfc',
                minHeight: 52,
                position: 'sticky',
                left: 0,
                zIndex: 10,
              }}
            />
            {weekDays.map((day, index) => {
              const isSelected = day.isSame(state.selectedDate, 'day')
              const dateKey = day.format('YYYY-MM-DD')
              const hasEvents = (singleDayEventsByDay.get(dateKey)?.length ?? 0) > 0 || hasSpanningEventOnDay(spanningLayout.segments, index)
              return (
                <div
                  key={index}
                  onClick={() => onDateChange(day)}
                  style={{
                    width: dayColumnWidths[index],
                    flexShrink: 0,
                    textAlign: 'center',
                    padding: '8px 0',
                    borderRight: '1px solid #e8e8e8',
                    borderBottom: '1px solid #e8e8e8',
                    backgroundColor: '#fafbfc',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: '#666',
                      marginBottom: 4,
                    }}
                  >
                    {day.format('ddd').toUpperCase()}
                  </div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      fontSize: 14,
                      fontWeight: 500,
                      backgroundColor: isSelected ? '#FF5C35' : 'transparent',
                      color: isSelected ? '#fff' : '#333',
                    }}
                  >
                    {day.date()}
                  </div>
                  {hasEvents && (
                    <div style={{ position: 'absolute', top: 8, right: 8 }}>
                      <OptimizeButton onClick={() => onOpenOptimize(day)} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div className={`scroll-shadow-wrapper${shadowLeft ? ' shadow-left' : ''}${shadowRight ? ' shadow-right' : ''}`}>
      <div
        ref={measureRef}
        style={{
          overflowX: 'auto',
          background: '#fff',
          border: '1px solid #e8e8e8',
          borderRight: 0,
          borderTop: 0,
        }}
      >
        <div style={{ minWidth: totalContentWidth, display: 'flex', flexDirection: 'column' }}>
          {hasSpanningItems && (
            <div
              style={{
                display: 'flex',
                height: spanningHeight + SPANNING_EVENT_GAP * 2,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: TIME_COLUMN_WIDTH,
                  flexShrink: 0,
                  borderRight: '1px solid #e8e8e8',
                  borderBottom: '1px solid #e8e8e8',
                  backgroundColor: '#fff',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                }}
              />
              <div style={{ position: 'relative', width: dayWidthsSum, flexShrink: 0 }}>
                <div style={{ display: 'flex', height: '100%' }}>
                  {weekDays.map((day, index) => (
                    <SpanningDropZone key={day.format('YYYY-MM-DD')} day={day} width={dayColumnWidths[index]} isResizing={isResizing} />
                  ))}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: dayWidthsSum,
                    height: '100%',
                    padding: `${SPANNING_EVENT_GAP}px 0`,
                    pointerEvents: 'none',
                  }}
                >
                  {spanningLayout.segments.map((segment) => (
                    <WeekViewSpanningEvent
                      key={`${segment.event.id}-${segment.weekIndex}`}
                      segment={segment}
                      weekStart={weekStart}
                      eventDisplayInfo={eventDisplayInfo}
                      dayColumnWidths={dayColumnWidths}
                      onEventClick={(eventId) => {
                        setSelectedEventId(eventId)
                        setDrawerOpen(true)
                      }}
                      onResize={handleSpanningEventResize}
                      onResizingChange={setIsResizing}
                    />
                  ))}
                  {spanningLayout.taskSegments.map((segment) => (
                    <WeekViewSpanningTask
                      key={`spanning-task-${segment.task.id}-${segment.weekIndex}`}
                      segment={segment}
                      weekStart={weekStart}
                      dayColumnWidths={dayColumnWidths}
                      onTaskClick={handleTaskClick}
                      onResize={handleSpanningTaskResize}
                      onResizingChange={setIsResizing}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {collapseEndHour > 0 && !earlyHoursExpanded && (
            <div
              onClick={() => setEarlyHoursExpanded(!earlyHoursExpanded)}
              style={{
                display: 'flex',
                cursor: 'pointer',
                borderBottom: '1px solid #e8e8e8',
                borderRight: '1px solid #e8e8e8',
                backgroundColor: '#fafbfc',
                userSelect: 'none',
              }}
            >
              <div
                style={{
                  width: TIME_COLUMN_WIDTH,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid #e8e8e8',
                  padding: '4px 0',
                  fontSize: 16,
                  color: '#999',
                  backgroundColor: '#fafbfc',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                }}
              >
                <FontAwesomeIcon icon={faArrowsFromDottedLine} />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 12px',
                  fontSize: 12,
                  color: '#999',
                }}
              >
                {formatCollapseLabel()}
              </div>
            </div>
          )}

          <div style={{ display: 'flex' }}>
            <div style={{ width: TIME_COLUMN_WIDTH, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#fff' }}>
              {Array.from({ length: visibleHours * 2 }, (_, i) => {
                const isHour = i % 2 === 0
                const hourLabel = isHour ? hours[i / 2] : null
                return (
                  <div
                    key={i}
                    style={{
                      height: HOUR_HEIGHT / 2,
                      borderRight: '1px solid #e8e8e8',
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                      paddingRight: 8,
                      fontSize: 11,
                      color: '#999',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {hourLabel}
                  </div>
                )
              })}
            </div>

            {weekDays.map((day, index) => (
              <div
                key={index}
                style={{
                  width: dayColumnWidths[index],
                  flexShrink: 0,
                  borderRight: '1px solid #e8e8e8',
                }}
              >
                <WeekViewDay
                  day={day}
                  events={singleDayEventsByDay.get(day.format('YYYY-MM-DD')) ?? []}
                  consultations={consultationsByDay.get(day.format('YYYY-MM-DD')) ?? []}
                  tasks={tasksByDay.get(day.format('YYYY-MM-DD')) ?? []}
                  eventDisplayInfo={eventDisplayInfo}
                  onEventClick={handleEventClick}
                  onConsultationClick={handleConsultationClick}
                  onTaskClick={handleTaskClick}
                  onEventResize={handleEventResize}
                  onConsultationResize={handleConsultationResize}
                  onTaskResize={handleTaskResize}
                  onResizingChange={setIsResizing}
                  isResizing={isResizing}
                  topOffset={topOffset}
                  onCalendarClick={handleCalendarClick}
                  onDragSelect={handleDragSelect}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>

      <JobDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSelectedEventId(null)
        }}
        eventData={selectedEventData}
        date={state.selectedDate}
        dateRange={state.visibleDateRange}
        readOnly={selectedEventId !== null && (eventDisplayInfo.get(selectedEventId)?.isDiscreteVisit ?? false)}
      />

      <EditConsultationModal
        open={consultationModalOpen}
        onClose={() => {
          setConsultationModalOpen(false)
          setSelectedConsultation(null)
        }}
        consultation={selectedConsultation}
      />

      <ScheduleEventTypePickerModal
        open={eventTypePickerState.open}
        onClose={() => setEventTypePickerState(prev => ({ ...prev, open: false }))}
        onScheduleJob={handleEventTypePickerScheduleJob}
        onAddTask={handleEventTypePickerAddTask}
      />

      <JobPickerModal
        open={jobPickerState.open}
        onClose={() => setJobPickerState(prev => ({ ...prev, open: false }))}
        onJobSelected={handleJobSelected}
      />
      <CreateEventFromJobModal
        open={createEventState.open}
        onClose={() => setCreateEventState(prev => ({ ...prev, open: false, job: null }))}
        job={createEventState.job}
        selectedDate={createEventState.day ?? undefined}
        selectedTime={createEventState.startMinutes}
        selectedDuration={createEventState.durationMinutes ?? undefined}
      />

      <TaskEventModal
        open={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false)
          setSelectedTask(null)
          setTaskModalDay(null)
        }}
        event={selectedTask}
        selectedDate={taskModalDay ?? undefined}
      />
    </div>
  )
})

export default ScheduleWeekView
